import { WixComponentTypes } from '@wix/shoutout-email-model-v3';
import { adjustImage } from '../../utils/adjust-image';
import { formatFooter, getClassicFooterHtmlBlocks } from './utils';
const assertNever = (wixFooterType) => {
    throw new Error(`Unhandled case: ${wixFooterType}`);
};
export const getWixFooterContentByType = (wixFooterType, content) => {
    const footerContent = wixFooterType === WixComponentTypes.hopp
        ? { ...content, helpCenter: undefined, helpCenterUrl: undefined }
        : content;
    const styleOverrides = wixFooterType === WixComponentTypes.hopp
        ? {
            fontSize: '16px',
            lineHeight: '1em',
            paragraphLineHeight: '1em',
        }
        : undefined;
    const { addressLine, linksLine, dontReplyLine } = getClassicFooterHtmlBlocks({
        content: footerContent,
        styleOverrides,
    });
    switch (wixFooterType) {
        case WixComponentTypes.payments:
            return formatFooter([dontReplyLine, linksLine, addressLine]);
        case WixComponentTypes.paymentsBrazil:
            return getWixPaymentsBrazilFooter(content);
        case WixComponentTypes.studio:
            return getWixStudioFooter(content);
        case WixComponentTypes.classic:
        case WixComponentTypes.wixel:
            return formatFooter([dontReplyLine, addressLine, linksLine]);
        case WixComponentTypes.rise:
            return formatFooter([dontReplyLine, linksLine, addressLine]);
        case WixComponentTypes.hopp:
            return formatFooter([dontReplyLine, addressLine, linksLine]);
        case WixComponentTypes.base44:
            return getBase44Footer(content);
        default:
            return assertNever(wixFooterType);
    }
};
export const BASE44_ICON_SIZE = 34;
const BASE44_TEXT_COLOR = '#A5A5A5';
// Pre-composed (gray circle + black glyph) icons, rendered as-is in display order.
const BASE44_SOCIAL_ICONS = [
    {
        hash: 'd2f3f5_3a325cc5a6984051bc344b92b29f0a7e~mv2.png',
        url: 'https://discord.com/invite/b44',
        alt: 'Discord',
    },
    {
        hash: 'd2f3f5_8c3b8d5a98e04142867c8130d67ebe94~mv2.png',
        url: 'https://x.com/Base44',
        alt: 'X',
    },
    {
        hash: 'd2f3f5_4b202e510e584dc5b840009fc4728dbd~mv2.png',
        url: 'https://www.facebook.com/people/Base44/61575154431579/?_rdr',
        alt: 'Facebook',
    },
    {
        hash: 'd2f3f5_4d6bea324705446597b5aac443dd6a72~mv2.png',
        url: 'https://www.instagram.com/base44.app/',
        alt: 'Instagram',
    },
    {
        hash: 'd2f3f5_8157e9b1146743e390e95b18b72e4031~mv2.png',
        url: 'https://www.linkedin.com/company/base44',
        alt: 'LinkedIn',
    },
];
// The social icons are rendered as React <img> elements by <SectionWixFooter>
// (outside SecureHtml, which strips <img> tags), so they are kept out of the
// HTML string below. This exposes the resolved (src, link) pairs to that component.
export const getBase44SocialIcons = () => BASE44_SOCIAL_ICONS.map(({ hash, url, alt }) => ({
    src: adjustImage(hash, {
        w: BASE44_ICON_SIZE * 2,
        h: BASE44_ICON_SIZE * 2,
    }),
    url,
    alt,
}));
function getBase44Footer(content) {
    const { dontReply, privacyPolicy, privacyPolicyUrl, termsOfUse, termsOfUseUrl, companyAddressLine, } = content;
    const textStyle = `color: ${BASE44_TEXT_COLOR}; font-size: 12px; line-height: 1.5em`;
    const dontReplyLine = dontReply
        ? `<p style="text-align: center;" class="so-global-p1"><span style="${textStyle}">${dontReply}</span></p>`
        : '';
    const spacer = `<p style="text-align: center; line-height: 0.5em; font-size: 8px;" class="so-global-p1"><span style="font-size: 9px;">&nbsp;</span></p>`;
    const links = [
        [privacyPolicy, privacyPolicyUrl],
        [termsOfUse, termsOfUseUrl],
    ].filter((linkParams) => Boolean(linkParams[0] && linkParams[1]));
    const separator = `<span style="${textStyle}"> | </span>`;
    const linkTags = links.map(([text, url]) => `<a target="_blank" rel="noopener noreferrer" href="${url}" style="${textStyle}; text-decoration: underline;">${text}</a>`);
    const linksLine = linkTags.length > 0
        ? `<p style="text-align: center;" class="so-global-p1">${linkTags.join(separator)}</p>`
        : '';
    const addressLine = companyAddressLine
        ? `<p style="text-align: center;" class="so-global-p1"><span style="${textStyle}">${companyAddressLine}</span></p>`
        : '';
    return formatFooter([dontReplyLine, spacer, linksLine, addressLine].filter(Boolean));
}
function getWixStudioFooter(content) {
    const { dontReply, helpCenter, helpCenterUrl, privacyPolicy, privacyPolicyUrl, termsOfUse, termsOfUseUrl, companyAddressLine, } = content;
    return `<div class="so-global-p1"><p style="text-align: left; line-height: 1.5em; font-size: 18px" class="so-global-p1"><span style="color: #2D2D2D"><span style="font-size: 18px"><span style="line-height: 1.5em"><span style="font-weight: bold">${dontReply}</span></span></span></span></p><p style="text-align: left; line-height: 0.5em; font-size: 8px" class="so-global-p1"><span style="color: #2D2D2D"><span style="font-size: 9px"><span style="line-height: 0.5em">&nbsp;</span></span></span></p><p style="text-align: left; line-height: 1.4em; font-size: 18px" class="so-global-p1"><a target="_blank" rel="noopener noreferrer" href="${helpCenterUrl}"><span style="color: #2D2D2D"><span style="font-size: 18px"><span style="line-height: 1.4em;border-right: 1px solid #2d2d2d;padding-right: 10px;">${helpCenter}</span></span></span></a><a target="_blank" rel="noopener noreferrer" href="${privacyPolicyUrl}"><span style="color: #2D2D2D"><span style="font-size: 18px"><span style="line-height: 1.4em;border-right: 1px solid #2d2d2d;padding-right: 10px;padding-left: 10px;">${privacyPolicy}</span></span></span></a><a target="_blank" rel="noopener noreferrer" href="${termsOfUseUrl}"><span style="color: #2D2D2D"><span style="font-size: 18px"><span style="line-height: 1.4em;padding-left: 10px;">${termsOfUse}</span></span></span></a></p><p style="text-align: left; line-height: 1.4em; font-size: 18px" class="so-global-p1"><span style="color: #2D2D2D"><span style="font-size: 18px"><span style="line-height: 1.4em;color: #2D2D2D">${companyAddressLine}</span></span></span></p></div>`;
}
const PAYMENTS_BRAZIL_COMMON_STYLE = 'color: #2D2D2D; font-size: 13px; line-height: 1.5';
const PAYMENTS_BRAZIL_LINK_STYLE = `${PAYMENTS_BRAZIL_COMMON_STYLE}; text-decoration: underline`;
const PAYMENTS_BRAZIL_SPACER = '<div style="height: 30px;">&nbsp;</div>';
function getWixPaymentsBrazilFooter(content) {
    const { paymentsBrazilCompliance, paymentsBrazilNeedHelp, paymentsBrazilAddressLine1, paymentsBrazilAddressLine2, paymentsBrazilAddressLine3, paymentsBrazilCompanyLine, paymentsBrazilTermsNotice, paymentsBrazilTermsOfServiceUrl, paymentsBrazilUnsupportedBusinessModelsUrl, paymentsBrazilVerificationRequirementsUrl, paymentsBrazilContactCustomerCareUrl, } = content;
    const getParagraph = (innerHtml, textAlign) => `<p style="text-align: ${textAlign}" class="so-global-p1"><span style="${PAYMENTS_BRAZIL_COMMON_STYLE}">${innerHtml}</span></p>`;
    const getBlock = (lines, textAlign) => formatFooter(lines.map((line) => getParagraph(line, textAlign)));
    const getLinkTag = (text, url) => `<a target="_blank" rel="noopener noreferrer" href="${url}" style="${PAYMENTS_BRAZIL_LINK_STYLE}">${text}</a>`;
    const replaceTransLinks = (text, urls) => {
        if (!text) {
            return undefined;
        }
        return urls.reduce((result, url, index) => result.replace(new RegExp(`<${index}>(.*?)</${index}>`, 'g'), (_match, linkText) => getLinkTag(linkText, url)), text);
    };
    const block1 = [
        replaceTransLinks(paymentsBrazilCompliance, [
            paymentsBrazilTermsOfServiceUrl || '',
            paymentsBrazilUnsupportedBusinessModelsUrl || '',
            paymentsBrazilVerificationRequirementsUrl || '',
        ]),
    ].filter((line) => Boolean(line));
    const block2 = [
        replaceTransLinks(paymentsBrazilNeedHelp, [
            paymentsBrazilContactCustomerCareUrl || '',
        ]),
    ].filter((line) => Boolean(line));
    const block3 = [
        paymentsBrazilAddressLine1,
        paymentsBrazilAddressLine2,
        paymentsBrazilAddressLine3,
        paymentsBrazilCompanyLine,
        replaceTransLinks(paymentsBrazilTermsNotice, [
            paymentsBrazilTermsOfServiceUrl || '',
        ]),
    ].filter((line) => Boolean(line));
    return [
        block1.length ? getBlock(block1, 'left') : '',
        block1.length && block2.length ? PAYMENTS_BRAZIL_SPACER : '',
        block2.length ? getBlock(block2, 'center') : '',
        block2.length && block3.length ? PAYMENTS_BRAZIL_SPACER : '',
        block3.length ? getBlock(block3, 'center') : '',
    ]
        .filter(Boolean)
        .join('');
}
//# sourceMappingURL=wix-footer-content.js.map